import React, { useRef, useState } from "react";
import { MobileMenu } from "features/shell";
import { getCookie } from "shared/auth/utils/cookieUtils";
import { parseApiError } from "shared/api/client/errorParser";
import { pairHeadsetWithCode } from "../api/device.api";
import { PAIRING_CODE_LENGTH as CODE_LENGTH, digitsFromPaste } from "../utils/pairingCode";

const MobilePairHeadsetPage: React.FC = () => {
  const [code, setCode] = useState(Array(CODE_LENGTH).fill(""));
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "">("");

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (value: string, idx: number) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newCode = [...code];
    newCode[idx] = value;
    setCode(newCode);

    if (value && idx < CODE_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const digits = digitsFromPaste(e.clipboardData.getData("text"));
    if (!digits) return;
    e.preventDefault();
    setCode(digits);
    setMessage("");
    setMessageType("");
    inputsRef.current[CODE_LENGTH - 1]?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const handlePairDevice = async () => {
    const codeString = code.join("");

    if (codeString.length !== CODE_LENGTH) {
      setMessage("Please enter the complete 6-digit code");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      const isSignedIn = Boolean(getCookie("userEmail") || getCookie("userToken"));

      if (!isSignedIn) {
        setMessage("Your session could not be found. Please log in again.");
        setMessageType("error");
        setIsLoading(false);
        return;
      }

      await pairHeadsetWithCode(codeString);

      setMessage("VR headset paired successfully!");
      setMessageType("success");

      setTimeout(() => {
        setCode(Array(CODE_LENGTH).fill(""));
        setMessage("");
        setMessageType("");
      }, 3000);
    } catch (error: any) {
      console.error("VR pairing failed:", error);
      setMessage(
        parseApiError(error, "Failed to pair VR headset. Please check your code.")
      );
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="menu-component-mobile">
      <div className="main-container-home-mobile">
        <div className="menu-component-header-mobile">
          <img
            src="/images/poetics-stream-lockup.svg"
            alt="Poetics Stream"
            className="quima-logo-mobile"
          />
          <img
            src="/images/mobile/hamburger-menu.svg"
            alt="menu"
            className="quima-menu-btn-mobile"
            onClick={toggleMenu}
          />
        </div>
        <div className="menu-component-mid-container-mobile">
          <div className="menu-component-mid-title-container-mobile">
            <div className="menu-component-title-mobile">Pair your headset</div>
            <div className="menu-component-subtitle-mobile">
              Pair headset to Poetics Stream with a 6-digit code
            </div>
          </div>
          <div className="menu-component-mid-title-container-mobile">
            <div className="menu-component-instruction-mobile">
              Enter the 6-digit code displayed in <br /> your headset, below:
            </div>
            <div className="code-inputs-container">
              {Array.from({ length: CODE_LENGTH }).map((_, idx) => (
                <input
                  key={idx}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={code[idx]}
                  onChange={(e) => handleChange(e.target.value, idx)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  onPaste={handlePaste}
                  aria-label={`Digit ${idx + 1}`}
                  ref={(el) => {
                    inputsRef.current[idx] = el;
                  }}
                  className="code-input-box"
                  autoFocus={idx === 0}
                  pattern="[0-9]*"
                  disabled={isLoading}
                />
              ))}
            </div>

            {message && (
              <div
                className={`message-container ${messageType}`}
                style={{
                  marginBottom: "16px",
                  padding: "12px",
                  borderRadius: "4px",
                  backgroundColor:
                    messageType === "success" ? "#d4edda" : "#f8d7da",
                  color: messageType === "success" ? "#155724" : "#721c24",
                  textAlign: "center",
                }}
              >
                {message}
              </div>
            )}

            <button
              className="menu-component-pair-btn-mobile"
              onClick={handlePairDevice}
              disabled={isLoading}
              style={{ cursor: "pointer" }}
            >
              {isLoading ? "Pairing..." : "Pair Device"}
            </button>
          </div>
        </div>
      </div>
      {isMenuOpen && (
        <MobileMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
      )}
    </div>
  );
};

export default MobilePairHeadsetPage;
